import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { getBenefitAuditLogs } from '../../../services/benefitReportService';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { formatDateTime } from '../../../utils/Utils';

const useBenefitAuditApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 50, pageIndex: 0 });
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 50 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    getBenefitAuditLogs(paramsToUse)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 50,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams]);

  const entityTypeOptions = useMemo(() => [
    { id: 'employee-benefit', name: t('benefitAuditLog.entity_employeeBenefit') },
    { id: 'dependent', name: t('benefitAuditLog.entity_dependent') },
    { id: 'application', name: t('benefitAuditLog.entity_application') },
    { id: 'life-event', name: t('benefitAuditLog.entity_lifeEvent') },
    { id: 'cafeteria-selection', name: t('benefitAuditLog.entity_cafeteriaSelection') },
    { id: 'pii-export', name: t('benefitAuditLog.entity_piiExport') },
    { id: 'audit-log', name: t('benefitAuditLog.entity_auditLog') },
  ], [t]);

  const actionOptions = useMemo(() => [
    { id: 'create', name: t('benefitAuditLog.action_create') },
    { id: 'update', name: t('benefitAuditLog.action_update') },
    { id: 'delete', name: t('benefitAuditLog.action_delete') },
    { id: 'view', name: t('benefitAuditLog.action_view') },
    { id: 'view-detail', name: t('benefitAuditLog.action_viewDetail') },
    { id: 'export', name: t('benefitAuditLog.action_export') },
    { id: 'request-removal', name: t('benefitAuditLog.action_requestRemoval') },
  ], [t]);

  const tagOptions = useMemo(() => [
    { id: 'pii-export', name: t('benefitAuditLog.tag_piiExport') },
    { id: 'audit-view', name: t('benefitAuditLog.tag_auditView') },
    { id: 'audit-drill-down', name: t('benefitAuditLog.tag_auditDrillDown') },
    { id: 'kvkk-sensitive', name: t('benefitAuditLog.tag_kvkkSensitive') },
  ], [t]);

  const labelOf = (list) => (val) => list.find((o) => o.id === val)?.name || val || '-';

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    (() => {
      const base = dateFilterColumn('timestamp', t('benefitAuditLog.timestamp'), lang, { includeTime: true });
      return {
        ...base,
        options: {
          ...base.options,
          customBodyRender: (v) => formatDateTime(v, lang),
        },
      };
    })(),
    {
      name: 'entityType',
      label: t('benefitAuditLog.entityType'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: { names: entityTypeOptions.map((o) => o.id), renderValue: labelOf(entityTypeOptions) },
        customFilterListOptions: { render: labelOf(entityTypeOptions) },
        customBodyRender: labelOf(entityTypeOptions),
      },
    },
    {
      name: 'action',
      label: t('benefitAuditLog.action'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: { names: actionOptions.map((o) => o.id), renderValue: labelOf(actionOptions) },
        customFilterListOptions: { render: labelOf(actionOptions) },
        customBodyRender: labelOf(actionOptions),
      },
    },
    {
      name: 'actorName',
      label: t('benefitAuditLog.actor'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      name: 'employeeName',
      label: t('benefitAuditLog.targetEmployee'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      name: 'tag',
      label: t('benefitAuditLog.tag'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: { names: tagOptions.map((o) => o.id), renderValue: labelOf(tagOptions) },
        customFilterListOptions: { render: labelOf(tagOptions) },
        customBodyRender: (v) => v || '-',
      },
    },
    {
      name: 'ipAddress',
      label: t('benefitAuditLog.ipAddress'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
  ], [t, lang, entityTypeOptions, actionOptions, tagOptions]);

  return { columns, data, fetching, pagination };
};

export default useBenefitAuditApi;
